/**
 * 人才盘点 · 个人报告 JSON（与 GET /reports/personal/:empId/summary 结构对齐）
 * 用于报表中心「示例预览」与「示例 PDF」；不依赖后端与批次数据。
 *
 * 设计准则（与 v1.0 定稿版对齐 · 见秦艳芳 mock 同源结构）：
 *   - 5 项素质维度：积极主动 / 敬业精神 / 卓越交付 / 坚韧抗压 / 先公后私
 *     与生产 EvaluationScore 中 INITIATIVE / DEDICATION / DELIVERY / RESILIENCE / PUBLIC_FIRST 一致。
 *   - 5 名评价人角色：自评 / 直属领导 / 斜线·带教 / 下属 / 同级管理者
 *     与生产 EvalRole 中 SELF / DIRECT / DIAGONAL / SUBORDINATE / PEER_LEADER 一致。
 *   - rowTotal / avgPerDim 直接由 RAW_SCORES 派生
 *   - radar.avg = 维度全部评价人（含自评）算术均值（与 personalSummary 实现一致）
 *   - dimensionComparison.othersAvg = 非自评（直属 / 斜线 / 下属 / 同级）均值，spread = 自评 − 非自评均值
 * 修改 RAW_SCORES 后所有派生量自动一致。
 */

export interface PersonalReportSummaryJson {
  emp: {
    id: number;
    name: string;
    hireDate?: string;
    deptPath?: string | null;
    position?: string | null;
    nineBoxResults?: Array<{
      qualityStandardScore?: unknown;
      qualityLevel?: unknown;
      performanceLevel?: unknown;
      finalCell?: unknown;
    }>;
    competencyAssessments?: Array<{
      weightedScore?: unknown | null;
      evaluator?: { name?: string | null };
      status?: unknown;
      submitAt?: unknown;
      redLines?: unknown;
    }>;
    highPotentialAsEvaluatee?: Array<{ resultLabel?: unknown }>;
  };
  radar: Array<{ code?: string; label?: string; avg: number }>;
  tasks?: number;
  insightsQualitative?: string[];
  dimensionComparison?: Array<{
    dimCode?: string;
    dimName: string;
    selfScore?: number | null;
    directScore?: number | null;
    othersAvg?: number | null;
    spread?: number | null;
    note: string;
  }>;
  evaluationBreakdown?: Array<{
    roleInEval?: string;
    roleLabel: string;
    evaluatorName: string;
    dimensions: Array<{ dimCode?: string; dimName: string; score: number; comment?: string | null }>;
    rowTotal: number;
    summaryLine?: string;
    avgPerDim?: number;
    submitAt?: string | Date | null;
  }>;
  qualityAnalysisMeta?: {
    advantageMeanThreshold: number;
    concernMeanThreshold: number;
    desc: string;
  };
  competencyRadar?: Array<{ axis: string; score: number; weight?: number }> | null;
  competencyAssessment?: {
    weightedScore?: number | null;
    evaluatorName?: string | null;
    status?: unknown;
    submitAt?: unknown;
    redLines?: unknown;
  } | null;
  performanceScore?: {
    score: number | null;
    level?: unknown;
    source?: unknown;
  } | null;
  /** 与导出 PDF「直属上级建议」同源；暂无业务字段时可省略 */
  leaderComment?: string | null;
  /** AI 生成的「综合画像 + 发展建议」（参考定位）；后端 GET summary 时附带，无则为 null */
  aiNarrative?: { profile: string; suggestions: string } | null;
  /** 是否已配置 AI 模型（控制「生成 AI 报告」按钮显示） */
  aiEnabled?: boolean;
  /** 单人 AI 队列状态：PENDING/RUNNING/READY/FAILED/NONE */
  aiQueueStatus?: 'NONE' | 'PENDING' | 'RUNNING' | 'READY' | 'FAILED';
}

const DIM_ROWS = [
  { code: 'INITIATIVE', name: '积极主动' },
  { code: 'DEDICATION', name: '敬业精神' },
  { code: 'DELIVERY', name: '卓越交付' },
  { code: 'RESILIENCE', name: '坚韧抗压' },
  { code: 'PUBLIC_FIRST', name: '先公后私' },
] as const;

type EvalRoleSlot = {
  roleLabel: string;
  evaluatorName: string;
  submitAt: string;
  scores: number[];
};

/** 5 个维度对应 5 名评价人的原始打分（标尺 0～7） · 李示例 HH 标杆 */
const RAW_SCORES: EvalRoleSlot[] = [
  {
    roleLabel: '自评',
    evaluatorName: '李示例（模拟）',
    submitAt: '2026-03-26T09:41:21.000Z',
    scores: [6.5, 6.0, 6.2, 5.8, 5.6],
  },
  {
    roleLabel: '直属领导',
    evaluatorName: '赵总监',
    submitAt: '2026-03-26T13:05:06.000Z',
    scores: [6.2, 6.4, 6.0, 6.3, 6.5],
  },
  {
    roleLabel: '斜线/带教',
    evaluatorName: '林导师',
    submitAt: '2026-03-26T13:52:41.000Z',
    scores: [5.8, 6.0, 5.9, 5.7, 6.0],
  },
  {
    roleLabel: '下属',
    evaluatorName: '王同事',
    submitAt: '2026-03-26T14:18:55.000Z',
    scores: [6.3, 6.5, 6.4, 6.1, 6.6],
  },
  {
    roleLabel: '同级管理者',
    evaluatorName: '周协作',
    submitAt: '2026-03-26T15:03:52.000Z',
    scores: [5.6, 5.8, 5.9, 5.5, 5.7],
  },
];

const COMMENTS_BY_ROLE: Record<string, Array<string | null>> = {
  自评: [
    '主动对齐公司战略，在「会员资产」一线项目里冲在前面，节奏拉得起来',
    '认同当前定位与公司价值观，按时按质交付，能为团队兜底',
    '关键交付节点能保证质量，超预期完成的占比 60% 以上',
    '面对突发与压力较稳，但偶尔在长周期议题上节奏会被打断',
    '会主动维护团队利益，但局部资源协调上偶有偏向自己业务线',
  ],
  直属领导: [
    '战略协同到位，能将集团目标拆解到业务节点',
    '敬业稳定，关键事项可直接交付，复盘报告质量在线',
    '执行闭环成熟，关键里程碑兑现率高',
    '坚韧抗压稳定，紧急事故处置稳健，复盘主动度强',
    '先公后私意识清晰，会主动让出资源支持优先级更高的议题',
  ],
  '斜线/带教': [
    '战略对齐稳，跨条线沟通中能讲清自己模块的价值露出',
    '敬业认真，对周报/月报投入度高，材料颗粒度细',
    '交付节奏稳健，跨条线协同的接口件维护清晰',
    '抗压可，长周期目标偶有疲态，需要节奏管理',
    '先公后私倾向稳，跨业务线让利动作明确',
  ],
  下属: [
    '管理风格主动，会带着团队抢节奏，团队体感很被推动',
    '高度敬业，是团队里加班最稳定也最有目标感的一位',
    '交付要求高，但也会给方法、给资源，团队跟得上',
    '抗压强，重要项目压力下还能为团队提供心理保护',
    '为团队争取资源主动，少有把团队短板暴露在跨部门会议上',
  ],
  同级管理者: [
    '战略对齐口径清晰，跨条线会议中能输出有共识价值的发言',
    '敬业，可以放心把跨业务的接口件托给她',
    '交付能兑现，但跨条线场合的方案权衡仍偏单线',
    '抗压稳，但跨条线议题上反应稍慢，可前置共识',
    '先公后私意愿足，但跨业务线的资源协调有时偏自己业务',
  ],
};

const round2 = (n: number) => Math.round(n * 100) / 100;
const round1 = (n: number) => Math.round(n * 10) / 10;

const buildBreakdown = () =>
  RAW_SCORES.map((r) => {
    const rowTotal = round1(r.scores.reduce((a, b) => a + b, 0));
    const avgPerDim = round2(rowTotal / DIM_ROWS.length);
    return {
      roleLabel: r.roleLabel,
      evaluatorName: r.evaluatorName,
      submitAt: r.submitAt,
      rowTotal,
      avgPerDim,
      dimensions: DIM_ROWS.map((dr, i) => ({
        dimCode: dr.code,
        dimName: dr.name,
        score: r.scores[i],
        comment: COMMENTS_BY_ROLE[r.roleLabel]?.[i] ?? null,
      })),
    };
  });

const breakdown = buildBreakdown();

const rowTotals = breakdown.map((b) => b.rowTotal);
const maxTotal = Math.max(...rowTotals);
const minTotal = Math.min(...rowTotals);

const breakdownWithSummary = breakdown.map((b) => {
  let trend = '与各评价人行合计相近';
  if (b.rowTotal === maxTotal) trend = '相对其他角色，本条行合计为最高的一档';
  else if (b.rowTotal === minTotal) trend = '相对其他角色，本条行合计为最低的一档';
  const summaryLine = `${b.roleLabel}「${b.evaluatorName}」打分覆盖 ${DIM_ROWS.length} 项素质维度，行合计 ${b.rowTotal.toFixed(1)}，折算单维均值约 ${b.avgPerDim.toFixed(2)} 分（标尺 0～7）。${trend}。以下为其各维度分值与评语。`;
  return { ...b, summaryLine };
});

const radar = DIM_ROWS.map((dr, i) => {
  const all = RAW_SCORES.map((r) => r.scores[i]);
  return {
    code: dr.code,
    label: dr.name,
    avg: round2(all.reduce((a, b) => a + b, 0) / all.length),
  };
});

const SELF_IDX = 0;
const DIRECT_IDX = 1;
const dimensionComparison = DIM_ROWS.map((dr, i) => {
  const dimName = dr.name;
  const selfScore = RAW_SCORES[SELF_IDX].scores[i];
  const directScore = RAW_SCORES[DIRECT_IDX].scores[i];
  const nonSelf = RAW_SCORES.filter((_, idx) => idx !== SELF_IDX).map((r) => r.scores[i]);
  const othersAvg = round2(nonSelf.reduce((a, b) => a + b, 0) / nonSelf.length);
  const spread = round2(selfScore - othersAvg);
  let note = '自评与非自评接近';
  if (spread > 0.5) note = '自评高于非自评均值';
  else if (spread < -0.5) note = '自评低于非自评均值';
  if (spread >= 1) note = '自评明显高于非自评均值';
  return {
    dimCode: dr.code,
    dimName,
    selfScore,
    directScore,
    othersAvg,
    spread,
    note,
  };
});

const insightsQualitative = [
  `各评价人素质项行合计极差约 ${round1(maxTotal - minTotal)} 分（最高 ${maxTotal.toFixed(1)} / 最低 ${minTotal.toFixed(1)}）；下属与直属视角给分相对最积极，同级管理者最审慎，整体收敛度较好。`,
  `自评与非自评在「积极主动」上分差最大（自评 ${RAW_SCORES[SELF_IDX].scores[0].toFixed(1)} vs 非自评均值 ${dimensionComparison[0].othersAvg?.toFixed(2)}），整体仍处合理区间，可作为对外标志性能力继续放大。`,
];

export const MOCK_PERSONAL_REPORT_SUMMARY: PersonalReportSummaryJson = {
  emp: {
    id: 0,
    name: '李示例（模拟）',
    hireDate: '2019-06-15',
    deptPath: '瑞小美总部 / 经营中台 / 产品技术中心',
    position: '高级产品经理',
    nineBoxResults: [
      {
        qualityStandardScore: 4.15,
        qualityLevel: 'H',
        performanceLevel: 'H',
        finalCell: 'HH',
      },
    ],
    competencyAssessments: [
      {
        weightedScore: 4.35,
        evaluator: { name: '赵总监' },
        status: 'SUBMITTED',
        submitAt: '2026-03-26T14:22:00.000Z',
        redLines: null,
      },
    ],
    highPotentialAsEvaluatee: [{ resultLabel: '高潜保持（建议纳入加速池复盘）' }],
  },
  radar,
  tasks: RAW_SCORES.length,
  insightsQualitative,
  dimensionComparison,
  evaluationBreakdown: breakdownWithSummary,
  qualityAnalysisMeta: {
    advantageMeanThreshold: 5.5,
    concernMeanThreshold: 4,
    desc:
      '报告中「优势 / 常规 / 短板」：仅依据本期各素质维度上所有已提交 360° 评价的算术均分，与阈值比较——均分 ≥ 5.5 标为「优势」，< 4 标为「短板」，介于其间为「常规」。该标签与高阶九宫格的标准分档位可能同源不同算，请以制度为准。',
  },
  competencyRadar: [
    { axis: '专业深度', score: 4.2 },
    { axis: '业务影响', score: 4.4 },
    { axis: '带教协同', score: 4.0 },
    { axis: '结果导向', score: 4.6 },
    { axis: '文化践行', score: 4.3 },
  ],
  competencyAssessment: {
    weightedScore: 4.35,
    evaluatorName: '赵总监',
    status: 'SUBMITTED',
    submitAt: '2026-03-26T14:22:00.000Z',
    redLines: null,
  },
  performanceScore: {
    score: 92,
    level: 'H',
    source: '月报汇总',
  },
  leaderComment:
    '李示例本期在「会员资产」一线项目上展示了清晰的战略对齐与责任担当，建议下半年承接 1 个跨部门规模化课题，并在「卓越交付」上沉淀复盘模板。',
  aiNarrative: {
    profile:
      '李示例是绩效与潜力同时在位的核心骨干，已具备承接更大业务范围的基础。其多源评价整体收敛，交付与协同形成稳定口碑；下一阶段的关键不是继续证明可靠，而是把个人方法沉淀为可复制的组织能力。',
    suggestions:
      '1. 在未来 90 天牵头一个跨部门关键议题，并沉淀可复用的里程碑与复盘模板。\n2. 每月与直属进行一次事实证据复盘，把优势维度转化为清晰的晋升材料。\n3. 为下一层级补足业务影响半径，至少完成一次面向管理层的成果表达。',
  },
  aiEnabled: true,
  aiQueueStatus: 'READY',
};
